import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { SessionService } from '@app/security/session.service';
import { Client, ProductGroup } from '@app/core/models/client';
import {
  FocusAiService,
  FocusAIUploadedFile,
  UploadingFile,
} from '../services/focus-ai.service';
import { User } from '@app/core/models/user';
import { interval, Subscription } from 'rxjs';
import { FocusAiFileItemComponent } from '../components/focus-ai-file-item/focus-ai-file-item.component';
import { FocusAiUploadingFileComponent } from '../components/focus-ai-uploading-file/focus-ai-uploading-file.component';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { FormInputStateControlComponent } from '@app/shared/form-input-state-control/form-input-state-control.component';
import { MessageClass } from '@app/core/models/message';
import { FormatUtils } from '@app/core/utils/format-utils';
import { CharacterLimits } from '@app/core/utils/characterLimits';
import { FeatureService } from '@app/core/services/feature.service';
import { FeatureFlags } from '@app/core/models/feature-flags';

@Component({
  selector: 'rn-focus-ai',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormInputStateControlComponent,
    MessageDialogComponent,
    FocusAiFileItemComponent,
    FocusAiUploadingFileComponent,
  ],
  templateUrl: './focus-ai.component.html',
  styleUrl: './focus-ai.component.scss',
})
export class FocusAiComponent implements OnInit {
  @ViewChild('focusAiGenerateMessageDialog', { static: true })
  focusAiGenerateMessageDialog: MessageDialogComponent;
  @ViewChild('focusAiApplyMessageDialog', { static: true })
  focusAiApplyMessageDialog: MessageDialogComponent;

  @Output() apply = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() focusAiMessage = new EventEmitter<MessageClass>();
  @Output() focusAiError = new EventEmitter<string>();
  @Input({ required: true }) messageId: string;
  @Input({ required: true }) emptyMessageContent: boolean;
  @Input({ required: true }) productGroup: ProductGroup;

  currentClient: Client;
  currentUser: User;
  hasUserAcceptedTermsAndConditions = false;
  promptCharLimit = CharacterLimits.PromptCharLimit;
  prompt = new FormControl<string>('', [
    Validators.maxLength(this.promptCharLimit),
  ]);

  prevState: {
    text: string;
    actions: { type: string; label: string; value: string }[];
  } = undefined;
  errorFilesInSession: FocusAIUploadedFile[] = [];
  uploadedFiles: FocusAIUploadedFile[] = [];
  uploadingFiles: UploadingFile[] = [];
  allowedExtensions = [
    '.doc',
    '.docx',
    '.pdf',
    '.txt',
    '.eml',
    '.html',
    '.ppt',
    '.pptx',
  ];
  contentProV2Enabled = false;

  private featureService = inject(FeatureService);
  private sessionService = inject(SessionService);
  protected focusAiService = inject(FocusAiService);

  private generatedMessageSignal = signal<MessageClass | null>(null);

  private pollingSubscription: Subscription;

  ngOnInit(): void {
    this.currentClient = this.sessionService.getCurrentUsersClient();
    this.currentUser = this.sessionService.currentUser;
    this.contentProV2Enabled = this.featureService.checkFlag(
      FeatureFlags.content_pro_v2,
    );
    this.checkUserAcceptedTermsAndConditions();
    this.pollForFiles();
  }

  ngOnDestroy(): void {
    this.focusAiService.reset(); // Reset the FocusAiService state

    // Unsubscribe from the polling subscription to prevent memory leaks
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  cancelFocusAi() {
    this.cancel.emit();
  }

  /**
   * This method is called when the user clicks the "Continue" button.
   * It checks if the terms and conditions are accepted, and if so, it calls the service to accept them.
   * After that, it re-checks if the user has accepted the terms and conditions.
   */
  acceptAndContinueToFocusAi() {
    if (!this.currentClient || !this.currentUser) return;

    this.focusAiService.acceptTermsAndConditions(
      this.currentUser?.id,
      this.currentClient?.id,
    );

    this.checkUserAcceptedTermsAndConditions();
  }

  /**
   * This method checks if the user has accepted the terms and conditions.
   * It uses the FocusAiService to check the acceptance status based on the user's id and client ID.
   */
  checkUserAcceptedTermsAndConditions() {
    this.focusAiService.deleteLegacyTermsCookie(
      this.currentUser?.email_address,
      this.currentClient?.id,
    );
    this.hasUserAcceptedTermsAndConditions =
      this.focusAiService.hasUserAcceptedTermsAndConditions(
        this.currentUser?.id,
        this.currentClient?.id,
      );
  }

  cancelFileSelection(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  dragOver(event) {
    event.preventDefault();
  }

  fetchFiles() {
    if (!this.currentClient || !this.messageId) return;

    this.focusAiService
      .getFiles(this.currentClient.id, this.messageId)
      .subscribe((files) => {
        if (files) {
          // Find files that have the same name as files in uploadingFiles and contain a reason (error)
          // from dynamo
          this.errorFilesInSession = files.data.filter((uploadedFile) => {
            // Check if file has a reason (indicating an error)
            if (!uploadedFile.reason) return false;

            // Check if there's a matching file name in uploadingFiles
            return this.uploadingFiles.some(
              (uploadingFile) => uploadingFile.file.name === uploadedFile.file,
            );
          });

          this.uploadedFiles = files.data.filter((item) => !item.reason);
          // filter out any files that have been successfully uploaded
          this.uploadingFiles = this.uploadingFiles.filter((item) => {
            return !files.data.some(
              (file) => file.file === item.file.name && !('error' in item),
            );
          });
        }
      });
  }

  pollForFiles() {
    this.fetchFiles();
    this.pollingSubscription = interval(3000).subscribe(() => {
      this.fetchFiles();
    });
  }

  removeUploadingFile(fileName: string) {
    this.uploadingFiles = this.uploadingFiles.filter(
      (item) => item.file.name !== fileName,
    );
  }

  // This function is used to prevent Angular from re-rendering the list unnecessarily
  trackByFile(index: number, file: FocusAIUploadedFile): string {
    return file.id; // Use a unique identifier for tracking
  }

  fileDropped(event) {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const filesArray: File[] = Array.from(event.dataTransfer.files);
      const validFiles: File[] = filesArray.filter((file) => {
        const ext = file.name
          .substring(file.name.lastIndexOf('.'))
          .toLowerCase();
        return this.allowedExtensions.includes(ext);
      });
      if (validFiles.length === 0) {
        console.warn(
          'No valid files dropped. Allowed types:',
          this.allowedExtensions.join(', '),
        );
        return;
      }
      // Create a FileList from validFiles
      const dataTransfer = new DataTransfer();
      validFiles.forEach((file) => dataTransfer.items.add(file));
      this.focusAiService
        .uploadFile(this.currentClient.id, this.messageId, dataTransfer.files)
        .subscribe({
          next: (response) => {
            this.uploadingFiles.push(...response);
          },
          error: (error) => {
            console.error('Error uploading files:', error);
          },
        });
    }
  }

  fileSelected(event) {
    if (event.target['files'].length > 0) {
      this.focusAiService
        .uploadFile(
          this.currentClient.id,
          this.messageId,
          event.target['files'],
        )
        .subscribe({
          next: (response) => {
            this.uploadingFiles.push(...response);
          },
          error: (error) => {
            console.error('Error uploading files:', error);
          },
        });
    }
  }

  generateFocusAiMessage() {
    if (this.prompt.invalid) return;

    this.focusAiService
      .generate(
        this.currentClient.id,
        this.messageId,
        this.prompt.value || '',
        this.productGroup.id,
        this.prevState,
      )
      .subscribe(
        (data: {
          text: string;
          actions: { type: string; label: string; value: string }[];
        }) => {
          this.prevState = data;
          const newMessage = new MessageClass({ showSmsByDefault: true });
          newMessage.wire.text = data.text;
          data.actions.forEach((action, index) => {
            const isPrimaryAction = index === 0;
            if (action.type === 'hyperlink') {
              newMessage.addLinkElement(
                this.currentClient.fdic_enabled,
                isPrimaryAction,
                action,
              );
            } else if (action.type === 'collapsible') {
              newMessage.addCollapsibleElement(action);
            } else if (action.type === 'call') {
              const val = FormatUtils.formatPhoneNumber(action.value);
              newMessage.addCallElement(isPrimaryAction, {
                ...action,
                value: val,
              });
            } else if (action.type === 'email') {
              newMessage.addEmailElement(isPrimaryAction, action);
            } else if (action.type === 'contact_card') {
              newMessage.addContactCardElement(isPrimaryAction, action);
            } else if (action.type === 'form_info_capture') {
              newMessage.addFormInfoCaptureElement(
                this.currentClient.feed_enabled,
                isPrimaryAction,
                action,
              );
            } else if (action.type === 'two_way') {
              newMessage.addTwoWayElement(isPrimaryAction, action);
            } else if (action.type === 'consent_upgrade') {
              if (this.productGroup?.consent?.in_wire_upgrade?.ts_cs) {
                newMessage.addIWCUElement(
                  this.productGroup.consent.in_wire_upgrade.ts_cs,
                  this.currentClient.feed_enabled,
                  isPrimaryAction,
                  action,
                );
              }
            }
          });
          this.generatedMessageSignal.set(newMessage);
          this.focusAiMessage.emit(newMessage);
        },
        (error) => {
          this.focusAiError.emit(
            error.error?.reason ||
              'An error occurred while generating the message. Please try again.',
          );
          this.generatedMessageSignal.set(null);
        },
      );
  }
  openGenerateMessageDialog() {
    this.focusAiGenerateMessageDialog?.showMessage();
  }
  closeGenerateMessageDialog() {
    this.focusAiGenerateMessageDialog?.cancelAction();
  }

  regenerateAiContent() {
    this.closeGenerateMessageDialog();
    this.generateFocusAiMessage();
  }
  checkIfAiGenerated() {
    this.generatedMessageSignal()
      ? this.openGenerateMessageDialog()
      : this.generateFocusAiMessage();
  }

  checkExistingContent() {
    !this.emptyMessageContent
      ? this.openApplyMessageDialog()
      : this.applyChanges();
  }

  openApplyMessageDialog() {
    this.focusAiApplyMessageDialog?.showMessage();
  }
  closeApplyMessageDialog() {
    this.focusAiApplyMessageDialog?.cancelAction();
  }

  applyChanges() {
    this.apply.emit();
    this.closeApplyMessageDialog();
  }
}
